import type { Translation } from "./en";
import tasksRu from "./ru.tasks";
import bugsRu from "./ru.bugs";
import notifyRu from "./ru.notify";
import analyticsRu from "./ru.analytics";
import workRu from "./ru.work";
import sprintsRu from "./ru.sprints";

const ru: Translation = {
  ...tasksRu,
  ...bugsRu,
  ...notifyRu,
  ...analyticsRu,
  ...workRu,
  ...sprintsRu,
  editor: {
    formatting: "Оформление",
    bold: "Жирный",
    italic: "Курсив",
    strike: "Зачёркнутый",
    code: "Код",
    link: "Ссылка",
    linkAddress: "Адрес ссылки",
    apply: "Применить",
    bulletList: "Маркированный список",
    orderedList: "Нумерованный список",
    taskList: "Чек-лист",
    quote: "Цитата",
    codeBlock: "Блок кода",
  },
  common: {
    bot: "бот",
    botHint: "Аккаунт интеграции (бот), а не человек",
    dismiss: "Скрыть",
    clear: "Очистить",
    more: "Другие действия",
    save: "Сохранить",
    add: "Добавить",
    delete: "Удалить",
    saving: "Сохранение…",
    cancel: "Отмена",
    close: "Закрыть",
    create: "Создать",
    edit: "Изменить",
    remove: "Удалить",
    copy: "Копировать",
    copied: "Скопировано",
    download: "Скачать",
    loading: "Загрузка…",
    continue: "Продолжить",
    back: "Назад",
    all: "Все",
    optional: "необязательно",
    actions: "Действия",
    never: "Никогда",
    retry: "Повторить",
    loadFailed: "Не удалось загрузить. Проверьте подключение и попробуйте снова.",
  },
  viewer: {
    zoomIn: "Увеличить",
    zoomOut: "Уменьшить",
    fit: "По размеру экрана",
    previous: "Предыдущий",
    next: "Следующий",
    openOriginal: "Открыть оригинал",
    counter: "{{n}} из {{total}}",
    hint: "Колесо или щипок — масштаб · перетаскивание — сдвиг · двойной клик — приблизить или отдалить · ← → — листать",
  },
  combobox: {
    create: "Создать «{{value}}»",
  },
  roles: {
    manager: "Менеджер",
    developer: "Разработчик",
    tester: "Тестировщик",
    viewer: "Наблюдатель",
  },
  locales: {
    en: "English",
    uz: "Oʻzbekcha",
    ru: "Русский",
  },
  theme: {
    light: "Светлая",
    dark: "Тёмная",
    system: "Системная",
  },
  errors: {
    unauthorized: "Сессия завершилась. Войдите снова.",
    forbidden: "У вас нет прав на это действие.",
    csrf: "Запрос заблокирован из соображений безопасности. Обновите страницу и попробуйте снова.",
    not_found: "Не найдено.",
    validation: "Некоторые поля заполнены неверно. Проверьте их и попробуйте снова.",
    conflict: "Такая запись уже существует.",
    invalid_credentials: "Неверный логин или пароль.",
    account_locked: "Слишком много неудачных попыток. Аккаунт заблокирован на 15 минут.",
    account_disabled: "Этот аккаунт отключён. Обратитесь к менеджеру.",
    invalid_code: "Неверный код. Проверьте его и попробуйте снова.",
    owner_protected: "Аккаунт владельца может изменить только сам владелец.",
    storage_unavailable: "Файловое хранилище сейчас недоступно. Попробуйте позже.",
    internal: "Что-то пошло не так на нашей стороне. Попробуйте снова.",
    network: "Не удаётся связаться с сервером. Проверьте подключение.",
  },
  auth: {
    signInTitle: "Вход в Mixla",
    signInHint: "Используйте логин и пароль, которые выдал вам менеджер.",
    username: "Логин",
    password: "Пароль",
    signIn: "Войти",
    language: "Язык",
    steps: {
      password: "Пароль",
      authenticator: "Аутентификатор",
      recovery: "Коды восстановления",
    },
    verifyTitle: "Двухфакторная аутентификация",
    verifyHint: "Введите 6-значный код из приложения-аутентификатора.",
    useRecovery: "Использовать код восстановления",
    useAuthenticator: "Использовать приложение-аутентификатор",
    recoveryLabel: "Код восстановления",
    recoveryHint: "Каждый код восстановления работает только один раз.",
    verify: "Подтвердить",
    otherAccount: "Войти в другой аккаунт",
    setupTitle: "Настройте двухфакторную аутентификацию",
    setupHint:
      "Обязательно для всех аккаунтов. Отсканируйте код в Google Authenticator, 1Password или любом TOTP-приложении.",
    cantScan: "Не удаётся отсканировать? Введите ключ",
    copyKey: "Скопировать ключ",
    enterCode: "Введите 6-значный код",
    verifyAndContinue: "Подтвердить и продолжить",
    qrLabel: "QR-код для приложения-аутентификатора",
    recoveryTitle: "Сохраните коды восстановления",
    recoveryIntro:
      "Если вы потеряете телефон, каждый из этих кодов позволит войти один раз. Храните их в надёжном месте — повторно они не покажутся.",
    copyAll: "Скопировать все",
    downloadTxt: "Скачать .txt",
    savedCheckbox: "Я сохранил(а) эти коды",
    finish: "Перейти в Mixla",
    digit: "Цифра {{n}} из 6",
    recoveryFileHeader: "Коды восстановления Mixla для {{username}}. Каждый код работает один раз.",
  },
  nav: {
    apiDocs: "Документация API",
    main: "Основное",
    projects: "Проекты",
    inbox: "Входящие",
    tasks: "Задачи",
    project: "Проект",
    overview: "Обзор",
    planning: "Планирование",
    reports: "Отчёты",
    members: "Участники",
    settings: "Настройки",
    admin: "Администрирование",
    users: "Пользователи",
    profile: "Профиль",
    switchProject: "Сменить проект",
    allProjects: "Все проекты",
    noProjects: "Проектов пока нет",
    openMenu: "Открыть меню",
  },
  projects: {
    title: "Проекты",
    newProject: "Новый проект",
    showArchived: "Показать архивные",
    emptyTitle: "Проектов пока нет",
    emptyManager: "Создайте первый проект, затем добавьте в него людей.",
    emptyMember: "Вы пока не состоите ни в одном проекте. Попросите менеджера добавить вас.",
    archived: "В архиве",
    membersCount: "Участники: {{count}}",
    yourRole: "Ваша роль",
    key: "Ключ",
    keyHint: "2–10 символов: заглавные латинские буквы и цифры, начинается с буквы. С него начинаются ключи задач (PAY-12). Изменить потом нельзя.",
    name: "Название",
    description: "Описание",
    created: "Проект создан",
  },
  overview: {
    majorBugs: "Открытые баги S1/S2",
    openBugs: "Всего открытых багов: {{count}}",
    overdueBugs: "Просроченные баги",
    toTest: "Тестировать",
    toFix: "Исправить",
    openTasks: "Открытые задачи",
    overdueTasks: "Просроченные задачи",
    bugsTitle: "Баги, требующие внимания",
    allBugs: "Все баги",
    tasksTitle: "Задачи в ожидании",
    allTasks: "Все задачи",
    peopleTitle: "У кого что",
    roundsTitle: "Раунды тестирования за неделю",
    noOpenBugs: "Открытых багов нет.",
    nothingWaiting: "Ничего не ждёт.",
    noRounds: "За последние 7 дней раундов не было.",
    person: "Сотрудник",
    bugsOwned: "Баги",
    longestWait: "Дольше всего ждёт",
    noDescription: "Описания пока нет.",
    archivedNotice: "Проект в архиве. Он скрыт из списка проектов, пока менеджер не вернёт его из архива.",
  },
  members: {
    title: "Участники",
    add: "Добавить участника",
    user: "Пользователь",
    platformRole: "Роль на платформе",
    projectRole: "Роль в этом проекте",
    sameAsPlatform: "Как на платформе",
    removeTitle: "Удалить {{name}} из проекта {{project}}?",
    removeBody: "Пользователь потеряет доступ к проекту. История сохранится.",
    added: "Участник добавлен",
    removed: "Участник удалён",
    updated: "Роль обновлена",
    empty: "Участников пока нет.",
    readOnly: "Изменять участников могут только менеджеры проекта.",
    noUsersLeft: "Все пользователи уже в проекте.",
    disabled: "Отключён",
    managerAlways: "Менеджеры платформы являются менеджерами во всех проектах.",
    pickUser: "Выберите пользователя",
  },
  settings: {
    title: "Настройки проекта",
    general: "Основное",
    workflow: "Процесс",
    templates: "Шаблоны",
    logo: "Логотип",
    logoHint: "PNG, JPEG или WebP, до 2 МБ.",
    uploadLogo: "Загрузить логотип",
    replaceLogo: "Заменить",
    removeLogo: "Удалить логотип",
    uploading: "Загрузка…",
    logoUploaded: "Логотип обновлён",
    logoRemoved: "Логотип удалён",
    logoTooBig: "Файл больше 2 МБ.",
    logoBadType: "Используйте изображение PNG, JPEG или WebP.",
    saved: "Настройки сохранены",
    archiveSection: "Архив",
    archiveBody:
      "Архивные проекты скрыты из списка проектов. Вернуть из архива можно в любой момент; ничего не удаляется.",
    archive: "Архивировать проект",
    unarchive: "Вернуть из архива",
    archiveTitle: "Архивировать {{name}}?",
    archivedToast: "Проект архивирован",
    unarchivedToast: "Проект возвращён из архива",
    forbidden: "Изменять настройки могут только менеджеры проекта.",
  },
  users: {
    title: "Пользователи",
    newUser: "Новый пользователь",
    filterRole: "Роль",
    filterStatus: "Статус",
    allRoles: "Все роли",
    allStatuses: "Все статусы",
    active: "Активен",
    disabled: "Отключён",
    name: "Имя",
    username: "Логин",
    role: "Роль",
    twoFactor: "2FA",
    status: "Статус",
    projects: "Проекты",
    lastLogin: "Последний вход",
    twoFactorOn: "Настроена",
    twoFactorOff: "Ещё нет",
    owner: "Владелец",
    you: "вы",
    reset: "Сбросить пароль и 2FA",
    resetTitle: "Сбросить пароль и 2FA для {{name}}?",
    resetBody:
      "Будет создан новый пароль, двухфакторная аутентификация сброшена, а все сессии пользователя завершены. При следующем входе он снова настроит 2FA.",
    resetConfirm: "Сбросить",
    createTitle: "Новый пользователь",
    displayName: "Отображаемое имя",
    usernameHint: "Строчные латинские буквы, цифры, точки, дефисы и подчёркивания.",
    language: "Язык",
    projectsLabel: "Проекты",
    projectsHint: "Отметьте проекты, в которые войдёт пользователь. В проекте можно назначить другую роль.",
    noProjectsYet: "Проектов пока нет — добавить пользователя в проекты можно позже.",
    roleInProject: "Роль в проекте",
    createButton: "Создать пользователя",
    created: "Пользователь создан",
    passwordTitle: "Пароль для {{username}}",
    passwordWarning: "Скопируйте его сейчас и передайте безопасным способом. Повторно он не покажется.",
    passwordNext: "При первом входе пользователь настроит двухфакторную аутентификацию.",
    done: "Готово",
    editTitle: "Изменить: {{name}}",
    accountDisabled: "Аккаунт отключён",
    accountDisabledHint: "Отключённые пользователи не могут войти. История сохраняется.",
    saved: "Пользователь сохранён",
    resetDone: "Пароль и 2FA сброшены",
    empty: "Нет пользователей, подходящих под фильтры.",
    ownerLocked: "Аккаунт владельца может изменить только сам владелец.",
    newBot: "Новый бот",
    filterKind: "Тип",
    allKinds: "Все",
    people: "Люди",
    bots: "Боты",
    access: "Доступ",
    lastActive: "Активность",
    tokenCount: "Токенов: {{count}}",
    tokens: "Токены",
    createBotTitle: "Новый бот",
    createBotButton: "Создать бота",
    botCreated: "Бот создан",
    botIntro: "Бот — это аккаунт для интеграции (CI, скрипты деплоя, мониторинг). У него есть роль и проекты, как у человека, но нет пароля: он работает только через API-токены, которые вы для него создаёте.",
    botTokensTitle: "Токены: {{name}}",
    botTokensIntro: "Всё, что использует эти токены, действует как @{{username}}, с его ролью и проектами. Храните их как пароли.",
    botDisabledTokens: "Бот отключён: его токены не работают, пока вы снова его не включите.",
  },
  profile: {
    title: "Профиль",
    account: "Аккаунт",
    displayName: "Отображаемое имя",
    username: "Логин",
    role: "Роль на платформе",
    preferences: "Настройки",
    language: "Язык",
    theme: "Тема",
    saved: "Профиль сохранён",
    changePassword: "Сменить пароль",
    currentPassword: "Текущий пароль",
    newPassword: "Новый пароль",
    confirmPassword: "Повторите новый пароль",
    passwordHint: "Не менее 12 символов.",
    passwordMismatch: "Пароли не совпадают.",
    passwordTooShort: "Используйте не менее 12 символов.",
    passwordChanged: "Пароль изменён",
    security: "Безопасность",
    sessions: "Сессии",
    logoutOthers: "Выйти на всех других устройствах",
    logoutOthersHint: "Завершает сессии во всех других браузерах и на устройствах. Текущая сессия останется.",
    loggedOutOthers: "Другие сессии завершены",
    logout: "Выйти",
  },
  notFound: {
    title: "Страница не найдена",
    body: "Такой страницы нет, или у вас нет к ней доступа.",
    home: "К проектам",
  },
};

export default ru;
